import { type Dictionary } from '@ngrx/entity';
import { type Store } from '@ngrx/store';
import { combineLatest, map, type Observable, of, switchMap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type ColumnId,
  getFormGroupLikeWrapper,
} from '@blaze-runtime/engine-domain';
import { getActiveBinding } from '@blaze-shared/elements-domain';
import { isDefined } from '@blaze-shared/shared-util';

import { getElementEntities } from '../../+state/elements/elements.selectors';
import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { isWizard } from '../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { isElementChildOfAtSlot } from '../../utils/layout/relationship';

import { getWizardStepLabelBySlotIndex } from './wizard.utils';

/**
 * What each of a wizard's forms last submitted, keyed `"<data source> (<step label>)"` — the
 * half of a step's visibility expression that comes from the wizard's own steps, merged with
 * the element's runtime context before the expression is evaluated.
 *
 * **Emits `{}` for a wizard with no forms**, rather than nothing at all: a questionnaire is
 * question-less until its builder adds one, and `combineLatest([])` completes without ever
 * emitting, which would leave every consumer of this stream waiting on a wizard that is
 * simply empty.
 */
export function selectWizardStepFormOutputs(
  store: Store,
  wizardId: CanvasElementId,
): Observable<Record<string, unknown>> {
  return store.select(getElementEntities).pipe(
    switchMap((elements) => {
      const wizard = elements[wizardId];
      if (!isWizard(wizard)) {
        return of({});
      }

      const formIds = getFormGroupLikeWrapper(getWrapperRefs(wizard)).map(({ formId }) => formId);
      const slotForms = wizard.slots.ids.flatMap((slotId) =>
        formIds
          .filter((formId) => isElementChildOfAtSlot(elements, formId, wizard.id, slotId))
          .map((formId) => ({ slotId, formId })),
      );
      if (!slotForms.length) {
        return of({});
      }

      return combineLatest(
        slotForms.map(({ slotId, formId }) =>
          store
            .select(runtimeSelectors.selectOutputUiById(formId, 'change'))
            .pipe(map((output) => ({ slotId, formId, output }))),
        ),
      ).pipe(
        map((results) =>
          Object.fromEntries(
            results
              .map(({ slotId, formId, output }) =>
                output
                  ? ([outputKey(elements, wizard, slotId, formId), output.data] as const)
                  : undefined,
              )
              .filter(isDefined),
          ),
        ),
      );
    }),
  );
}

/** How a step's form data is addressed from a visibility expression. */
function outputKey(
  elements: Dictionary<CanvasElement>,
  wizard: CanvasElementFrom<'wizard'>,
  slotId: ColumnId,
  formId: CanvasElementId,
): string {
  const form = elements[formId];
  const name = hasDataProps(form)
    ? getActiveBinding(form.props.data, 'value')?.dataSource?.name
    : undefined;
  const stepLabel = getWizardStepLabelBySlotIndex(wizard.props, wizard.slots.ids.indexOf(slotId));
  return `${name ?? '???'} (${stepLabel ?? '???'})`;
}
